import React, { useMemo } from 'react';
import { Alert, Button, Space, Tag, Typography } from 'antd';

const { Text } = Typography;

export interface EpcEstimateAttentionPanelProps {
  readiness?: Record<string, unknown> | null;
  diagnostics?: Array<{ code: string; severity: string; message: string }>;
  boqCoverage?: {
    pct_costs_linked?: number;
    unlinked_cost_line_count?: number;
    boq_without_cost_count?: number;
    orphan_cost_line_count?: number;
  } | null;
  /** UX3.1 — chip row instead of stacked alerts */
  compact?: boolean;
  onFilterUnlinked?: () => void;
  onFilterBoqWithoutCost?: () => void;
  onOpenDiagnostics?: () => void;
}

export const EpcEstimateAttentionPanel: React.FC<EpcEstimateAttentionPanelProps> = ({
  readiness,
  diagnostics = [],
  boqCoverage,
  compact,
  onFilterUnlinked,
  onFilterBoqWithoutCost,
  onOpenDiagnostics,
}) => {
  const errorCount = useMemo(() => {
    const blockers = (readiness as { blockers?: unknown[] })?.blockers;
    const blockerN = Array.isArray(blockers) ? blockers.length : 0;
    const diagN = diagnostics.filter((x) => x.severity === 'error').length;
    return blockerN + diagN;
  }, [readiness, diagnostics]);

  const warnCount = useMemo(() => {
    let n = diagnostics.filter((x) => x.severity === 'warn').length;
    if (boqCoverage?.unlinked_cost_line_count) n += 1;
    if (boqCoverage?.boq_without_cost_count) n += 1;
    return n;
  }, [diagnostics, boqCoverage]);

  const items = useMemo(() => {
    const out: Array<{ key: string; type: 'error' | 'warning' | 'info'; message: string }> = [];
    const blockers = (readiness as { blockers?: Array<{ code?: string; message?: string }> })?.blockers;
    if (Array.isArray(blockers)) {
      for (const b of blockers) {
        out.push({ key: `b-${b.code || b.message}`, type: 'error', message: b.message || b.code || 'Blocker' });
      }
    }
    for (const d of diagnostics.filter((x) => x.severity === 'error')) {
      out.push({ key: d.code, type: 'error', message: d.message });
    }
    if (boqCoverage?.unlinked_cost_line_count) {
      out.push({
        key: 'unlinked',
        type: 'warning',
        message: `${boqCoverage.unlinked_cost_line_count} direct cost line(s) not linked to client BOQ`,
      });
    }
    if (boqCoverage?.boq_without_cost_count) {
      out.push({
        key: 'boq-no-cost',
        type: 'warning',
        message: `${boqCoverage.boq_without_cost_count} BOQ line(s) without linked cost`,
      });
    }
    for (const d of diagnostics.filter((x) => x.severity === 'warn')) {
      if (!out.some((i) => i.key === d.code)) {
        out.push({ key: d.code, type: 'warning', message: d.message });
      }
    }
    return out.slice(0, 8);
  }, [readiness, diagnostics, boqCoverage]);

  if (!items.length) return null;

  if (compact) {
    return (
      <Space wrap size={[8, 8]} style={{ marginBottom: 8 }}>
        {errorCount > 0 ? (
          <Tag color="error">{errorCount} blocker(s)</Tag>
        ) : null}
        {boqCoverage?.unlinked_cost_line_count ? (
          <Button size="small" type="link" onClick={onFilterUnlinked}>
            {boqCoverage.unlinked_cost_line_count} unlinked cost(s) →
          </Button>
        ) : null}
        {boqCoverage?.boq_without_cost_count ? (
          <Button size="small" type="link" onClick={onFilterBoqWithoutCost}>
            {boqCoverage.boq_without_cost_count} BOQ without cost →
          </Button>
        ) : null}
        {warnCount > 0 && !boqCoverage?.unlinked_cost_line_count && !boqCoverage?.boq_without_cost_count ? (
          <Tag color="warning">{warnCount} warning(s)</Tag>
        ) : null}
        {onOpenDiagnostics ? (
          <Button size="small" type="link" onClick={onOpenDiagnostics}>
            Diagnostics
          </Button>
        ) : null}
      </Space>
    );
  }

  return (
    <div style={{ marginBottom: 12 }}>
      {items.map((item) => (
        <Alert
          key={item.key}
          type={item.type}
          showIcon
          message={<Text>{item.message}</Text>}
          style={{ marginBottom: 6 }}
        />
      ))}
    </div>
  );
};
